import {
  createContext,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import OnboardingModal from "@/components/onboarding/shared/OnboardingModal/OnboardingModal";

type ShowOnboardingModalOptions = {
  onBeforeChoose?: () => Promise<boolean> | boolean;
};

type OnboardingModalContextType = {
  showOnboardingModal: (options?: ShowOnboardingModalOptions) => void;
};

const OnboardingModalContext = createContext<OnboardingModalContextType | null>(
  null
);

export function useOnboardingModal() {
  const context = useContext(OnboardingModalContext);
  if (!context) {
    throw new Error(
      "useOnboardingModal must be used within OnboardingModalProvider"
    );
  }
  return context;
}

export function OnboardingModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const onBeforeChooseRef = useRef<
    ShowOnboardingModalOptions["onBeforeChoose"] | undefined
  >(undefined);
  const navigate = useNavigate();

  const showOnboardingModal = (options?: ShowOnboardingModalOptions) => {
    onBeforeChooseRef.current = options?.onBeforeChoose;
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    onBeforeChooseRef.current = undefined;
  };

  const handleChoose = async (path: string) => {
    const beforeChoose = onBeforeChooseRef.current;
    closeModal();
    if (beforeChoose) {
      const ok = await beforeChoose();
      if (!ok) return;
    }
    navigate(path);
  };

  return (
    <OnboardingModalContext.Provider value={{ showOnboardingModal }}>
      {children}
      <OnboardingModal
        isOpen={isOpen}
        onClose={closeModal}
        onChooseWizard={() => handleChoose("/identificare-raportare")}
        onChooseManual={() => handleChoose("/onboarding/formular-notificare")}
        onChooseUpload={() =>
          handleChoose("/onboarding/formular-notificare?intent=upload")
        }
      />
    </OnboardingModalContext.Provider>
  );
}
